import React, { useState, useEffect } from "react";
import {
  Container,
  Card,
  Badge,
  Spinner,
  Alert,
  Form,
  InputGroup,
  Row,
  Col,
  Nav,
} from "react-bootstrap";
import { Ticket, Search, Calendar, Tag, AlertCircle, Paperclip } from "lucide-react";
import { useTranslation } from "react-i18next";
import { getTicketsByClientAndUser } from "../../api/apiClient";
import TicketDetailModal from "./ticketDetailModal";
import ListPagination from "../utils/ListPagination";

// Mapeo de estados
const STATUS_OPTIONS = [
  { value: "all", labelKey: "tickets.statusAll" },
  { value: 0, labelKey: "tickets.statusNew" },
  { value: 1, labelKey: "tickets.statusInProgress" },
  { value: 2, labelKey: "tickets.statusResolved" },
  { value: 3, labelKey: "tickets.statusClosed" },
];

// Mapeo de categorías
const CATEGORY_LABELS = {
  0: "tickets.categoryErrors",
  1: "tickets.categoryInquiries",
  2: "tickets.categorySupportRequest",
  3: "tickets.categoryOther",
  4: "tickets.categoryTechnical",
};

// Colores para badges de estado
const STATUS_COLORS = {
  0: "secondary", // Nuevo
  1: "primary", // En Progreso
  2: "success", // Resuelto
  3: "danger", // Cerrado
};

const MyTickets = () => {
  const { t } = useTranslation();
  const [tickets, setTickets] = useState([]);
  const [filteredTickets, setFilteredTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("all");
  const [searchText, setSearchText] = useState("");
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [showModal, setShowModal] = useState(false);

  // Cargar tickets al montar el componente
  useEffect(() => {
    loadTickets();
  }, []);

  // Filtrar tickets cuando cambia el estado seleccionado o el texto de búsqueda
  useEffect(() => {
    filterTickets();
  }, [tickets, selectedStatus, searchText]);

  const loadTickets = async () => {
    setLoading(true);
    setError("");

    try {
      const clientId = sessionStorage.getItem("clientId");
      const idUser = sessionStorage.getItem("idUser");

      if (!clientId || !idUser) {
        setError(t("tickets.errorNoClient"));
        setLoading(false);
        return;
      }

      const response = await getTicketsByClientAndUser(
        parseInt(clientId, 10),
        parseInt(idUser, 10)
      );

      if (response.success) {
        setTickets(response.data || []);
      } else {
        // Si no hay tickets, no es un error, simplemente no hay datos
        if (response.data && Array.isArray(response.data) && response.data.length === 0) {
          setTickets([]);
        } else {
          setError(response.message || t("tickets.errorLoading"));
        }
      }
    } catch (ex) {
      console.error("Error loading tickets:", ex);
      setError(t("tickets.errorLoading"));
    } finally {
      setLoading(false);
    }
  };

  const filterTickets = () => {
    let filtered = [...tickets];

    // Filtrar por estado
    if (selectedStatus !== "all") {
      filtered = filtered.filter(
        (ticket) => ticket.status === parseInt(selectedStatus, 10)
      );
    }

    // Filtrar por texto de búsqueda
    if (searchText.trim()) {
      const searchLower = searchText.toLowerCase();
      filtered = filtered.filter(
        (ticket) =>
          ticket.title?.toLowerCase().includes(searchLower) ||
          ticket.description?.toLowerCase().includes(searchLower)
      );
    }

    setFilteredTickets(filtered);
  };

  const handleTicketClick = (ticket) => {
    setSelectedTicket(ticket);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedTicket(null);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  if (loading) {
    return (
      <Container className="py-5 d-flex justify-content-center">
        <div className="text-center text-white">
          <Spinner animation="border" size="lg" className="mb-3" />
          <p>{t("tickets.loading")}</p>
        </div>
      </Container>
    );
  }

  return (
    <Container className="py-4" style={{ maxWidth: "900px" }}>
      {/* Header */}
      <div className="d-flex align-items-center gap-2 mb-4">
        <Ticket size={24} className="text-white" />
        <h4 className="text-white mb-0 fw-bold">{t("tickets.pageTitle")}</h4>
      </div>

      {/* Filtros y búsqueda */}
      <Card
        className="mb-3"
        style={{
          borderRadius: "15px",
          border: "none",
          background: "rgba(255,255,255,0.15)",
          backdropFilter: "blur(10px)",
        }}
      >
        <Card.Body className="p-3">
          <Row className="g-3">
            {/* Búsqueda */}
            <Col xs={12} md={6}>
              <InputGroup
                style={{
                  borderRadius: "10px",
                  overflow: "hidden",
                }}
              >
                <InputGroup.Text
                  style={{
                    background: "rgba(255,255,255,0.9)",
                    border: "none",
                  }}
                >
                  <Search size={16} />
                </InputGroup.Text>
                <Form.Control
                  type="text"
                  placeholder={t("tickets.searchPlaceholder")}
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  style={{
                    background: "rgba(255,255,255,0.9)",
                    border: "none",
                  }}
                />
              </InputGroup>
            </Col>

            {/* Filtro de estado */}
            <Col xs={12} md={6}>
              <Form.Select
                value={selectedStatus}
                onChange={(e) => setSelectedStatus(e.target.value)}
                style={{
                  borderRadius: "10px",
                  background: "rgba(255,255,255,0.9)",
                  border: "none",
                }}
              >
                {STATUS_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {t(option.labelKey)}
                  </option>
                ))}
              </Form.Select>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Mensaje de error */}
      {error && (
        <Alert variant="danger" className="mb-3">
          {error}
        </Alert>
      )}

      {/* Lista de tickets */}
      {filteredTickets.length === 0 ? (
        <Card
          style={{
            borderRadius: "20px",
            border: "none",
            background: "rgba(255,255,255,0.15)",
            backdropFilter: "blur(10px)",
          }}
        >
          <Card.Body className="p-5 text-center">
            <AlertCircle size={48} className="text-white mb-3 opacity-50" />
            <p className="text-white mb-0">
              {searchText || selectedStatus !== "all"
                ? t("tickets.noResultsFiltered")
                : t("tickets.noTickets")}
            </p>
          </Card.Body>
        </Card>
      ) : (
        <ListPagination
          items={filteredTickets}
          itemsPerPageOptions={[5, 10, 15, 20]}
          defaultItemsPerPage={10}
          itemLabel="tickets.ticketsLabel"
          containerClassName="d-flex flex-column gap-3"
          renderItem={(ticket) => (
            <Card
              key={ticket.id}
              onClick={() => handleTicketClick(ticket)}
              style={{
                borderRadius: "15px",
                border: "none",
                background: "rgba(255,255,255,0.15)",
                backdropFilter: "blur(10px)",
                cursor: "pointer",
                transition: "all 0.2s ease",
              }}
              className="ticket-card"
            >
              <Card.Body className="p-3">
                <div className="d-flex justify-content-between align-items-start mb-2">
                  <div className="flex-grow-1">
                    <div className="d-flex align-items-center gap-2 mb-1">
                      <span
                        className="badge bg-dark"
                        style={{ fontSize: "0.7rem", fontFamily: "monospace" }}
                      >
                        {ticket.ticketNumber}
                      </span>
                    </div>
                    <h6 className="text-white fw-bold mb-0">
                      {ticket.title}
                    </h6>
                  </div>
                  <Badge bg={STATUS_COLORS[ticket.status]} className="ms-2">
                    {t(`tickets.status${ticket.status}`)}
                  </Badge>
                </div>

                <div className="d-flex flex-wrap gap-2 mt-2">
                  {/* Categoría */}
                  <div
                    className="d-flex align-items-center gap-1"
                    style={{ fontSize: "0.85rem", color: "rgba(255,255,255,0.8)" }}
                  >
                    <Tag size={14} />
                    <span>{t(CATEGORY_LABELS[ticket.category] || "tickets.categoryOther")}</span>
                  </div>

                  {/* Fecha */}
                  <div
                    className="d-flex align-items-center gap-1"
                    style={{ fontSize: "0.85rem", color: "rgba(255,255,255,0.8)" }}
                  >
                    <Calendar size={14} />
                    <span>{formatDate(ticket.createdAt)}</span>
                  </div>

                  {/* Adjuntos */}
                  {ticket.attachments && ticket.attachments.length > 0 && (
                    <div
                      className="d-flex align-items-center gap-1"
                      style={{ fontSize: "0.85rem", color: "rgba(255,255,255,0.8)" }}
                    >
                      <Paperclip size={14} />
                      <span>{ticket.attachments.length} {ticket.attachments.length === 1 ? t("tickets.attachment") : t("tickets.attachments")}</span>
                    </div>
                  )}
                </div>
              </Card.Body>
            </Card>
          )}
        />
      )}

      {/* Modal de detalle */}
      {selectedTicket && (
        <TicketDetailModal
          show={showModal}
          onHide={handleCloseModal}
          ticket={selectedTicket}
        />
      )}

      {/* Estilos adicionales */}
      <style>{`
        .ticket-card:hover {
          background: rgba(255,255,255,0.25) !important;
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(0,0,0,0.15);
        }
      `}</style>
    </Container>
  );
};

export default MyTickets;
